import { Dialog, DialogContent } from "components/Dialog";
import { MouseEvent, ReactNode, RefObject, useId, useRef } from "react";
import "./AutomationsTabDialog.css";

/**
 * Gives a clicked field the focus as the mouse goes down. A select losing
 * the focus takes parts of itself off the page, and the dialog's focus trap,
 * seeing that while the focus is between two fields, pulls it back to the
 * dialog: the field never gets it, and what is typed next is lost. Moved at
 * once, it is never between them. A select opens its own list on this press,
 * so it keeps its own input.
 */
function focusFieldAtOnce(e: MouseEvent<HTMLElement>) {
  const field = (e.target as Element).closest<HTMLElement>(
    'input:not([role="combobox"]), textarea, [contenteditable="true"]'
  );
  if (field && field !== document.activeElement)
    field.focus({ preventScroll: true });
}

/**
 * The dialog the step and trigger editors share: the app's dialog at one
 * width, its title at the top, its buttons at the bottom, and the fields
 * between them, which scroll when they are taller. fixed: one height too,
 * so switching what a step does never changes its size. What a field opens
 * (its list, Assign to, Insert variable, When) opens under it, inside the
 * dialog. Its close button, like Cancel, drops the changes; Escape and a
 * click outside close it only when there are none, else onWarn.
 */
export default function EditorDialog({
  title,
  fixed = false,
  dirty,
  onClose,
  onWarn,
  returnFocus,
  bodyRef,
  footer,
  children,
}: {
  title: string;
  fixed?: boolean;
  dirty: boolean;
  onClose: () => void;
  /** Escape or a click outside while there are changes. */
  onWarn: () => void;
  /** What opened it, which gets the focus back if it is still there. */
  returnFocus?: HTMLElement | null;
  bodyRef?: RefObject<HTMLDivElement>;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        ref={contentRef}
        aria-labelledby={titleId}
        aria-describedby={undefined}
        className={`dialog-content automation-dialog${
          fixed ? " automation-dialog--fixed" : ""
        }`}
        onMouseDownCapture={focusFieldAtOnce}
        // The dialog takes the focus, not its first field.
        onOpen={(e: Event) => {
          e.preventDefault();
          contentRef.current?.focus({ preventScroll: true });
        }}
        onCloseAutoFocus={(e: Event) => {
          e.preventDefault();
          if (returnFocus?.isConnected)
            returnFocus.focus({ preventScroll: true });
        }}
        onEscapeKeyDown={(e: KeyboardEvent) => {
          // A select's own Escape closes its list, not the dialog.
          if (e.defaultPrevented) return;
          if (dirty) {
            e.preventDefault();
            onWarn();
          }
        }}
        onInteractOutside={(e: Event) => {
          if (dirty) {
            e.preventDefault();
            onWarn();
          }
        }}
      >
        <h2
          id={titleId}
          className="flex-none px-6 pb-3 pr-12 pt-4 text-xl font-semibold text-neutral-900"
        >
          {title}
        </h2>
        <div
          ref={bodyRef}
          className="min-h-0 flex-1 overflow-y-auto px-6 pb-5 pt-1 text-sm"
        >
          {children}
        </div>
        {footer && (
          <div className="flex-none border-t border-gray-100 px-6 pb-4 pt-3">
            {footer}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
